import React, { useState, memo, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { CSSTransition } from 'react-transition-group';
import classnames from 'classnames';
import { useFlags } from 'launchdarkly-react-client-sdk';

// Constants
import { IntegrationsList as IntegrationsListConstant } from 'Shared/constants/IntegrationsList.constants';

// Types
import { QuickStartGuideItemIDs, QuickStartGuideItems } from './QuickStartGuideItems.constants';

// Hooks
import { useToggle } from 'Shared/utils/hooks';
import useUpdateQuickStartGuideFlag from './useUpdateQuickStartGuideFlag';
import useWindowDimensions from 'Wpds/hooks/useWindowDimensions';
import { useCurrentUser } from 'Shared/utils/hooks/use-current-user';
import { useApp } from 'Shared/utils/hooks/use-app';

// Utils
import { trackClickQuickStartGuide, trackClickQuickStartGuideItem } from 'Shared/tracking/tracking';

// Components
import WpIcon from 'Wpds/components/icon/WpIcon';
import { Portal } from 'Wpds/components/portal/Portal';
import QsgList from './partials/list/QsgList';
import QsgStep from './partials/step/QsgStep';
import QuickStartGuideCompletionModal from './QuickStartGuideCompletionModal';

// Styles
import './QuickStartGuide.scss';

const QuickStartGuide = () => {
  const [isCompleteModalOpen, toggleIsCompleteModalOpen] = useToggle(false);
  const [activeStepId, setActiveStepId] = useState('start');
  const { quickStartGuideV2: showQuickStartGuide } = useFlags();
  const { width: windowWidth } = useWindowDimensions();

  const isMobile = windowWidth < 769;
  const { isZoom } = useApp();
  const { user } = useCurrentUser();
  const onboardingCompleteTime = user.onboarding.completed_at;

  const {
    isOpen,
    isComplete,
    isDismissed,
    quickStartGuideData,
    handleCompleteItem,
    openQuickStartGuide,
    closeQuickStartGuide,
    updateIsComplete,
  } = useUpdateQuickStartGuideFlag();

  const numberOfItemsComplete = Object.keys(quickStartGuideData).length;
  const isCompletedAllItems = numberOfItemsComplete === QuickStartGuideItems.length - 1; // minus 1 to account for the dismiss step

  const dataSources = useSelector(state => state.data_sources).filter(
    ds => IntegrationsListConstant[ds.slug]?.showOnIntegrationsPage !== false
  );

  const notificationDestinations = useSelector(state =>
    state.notification_destinations.filter(
      nd => nd.slug === 'slack' || nd.slug === 'microsoft_teams' || nd.slug === 'google_chat'
    )
  ).map(nd => {
    return { ...nd, ...{ connected: nd.enabled || nd.organization_notification_destination_connected } };
  });

  const mergedSources = [...dataSources, ...notificationDestinations];

  const calendarIntegrations = mergedSources.filter(ds => ds.isCalendar && ds.connected);
  const otherIntegratoins = mergedSources.filter(ds => !ds.isCalendar && ds.connected);

  const baseClassName = 'QuickStartGuide';
  const listPanelClasses = classnames(
    `${baseClassName}__panel__item`,
    `${baseClassName}__panel__list`,
    activeStepId !== 'start' && `${baseClassName}__panel__list--step-open`
  );

  const toggleIsOpen = () => {
    if (isOpen) {
      closeQuickStartGuide();
      trackClickQuickStartGuide('close');
    } else {
      openQuickStartGuide();
      trackClickQuickStartGuide('open');
    }
  };

  useEffect(() => {
    if (calendarIntegrations.length > 0 && !quickStartGuideData[QuickStartGuideItemIDs.syncCalendar]) {
      handleCompleteItem(QuickStartGuideItemIDs.syncCalendar);
    }
    if (otherIntegratoins.length > 0 && !quickStartGuideData[QuickStartGuideItemIDs.addIntegrations]) {
      handleCompleteItem(QuickStartGuideItemIDs.addIntegrations);
    }
  }, []);

  useEffect(() => {
    const isSomethingTrue = Object.values(quickStartGuideData).includes('complete');
    if (isOpen && !isCompleteModalOpen && isCompletedAllItems && isSomethingTrue) toggleIsCompleteModalOpen();
  }, [isOpen, isCompletedAllItems, quickStartGuideData]);

  useEffect(() => {
    if (isCompletedAllItems && !isComplete) updateIsComplete();
  }, [isCompletedAllItems, quickStartGuideData, isComplete]);

  useEffect(() => {
    if (!(activeStepId == 'start' || activeStepId === 'dismiss'))
      trackClickQuickStartGuideItem(activeStepId as QuickStartGuideItemIDs);
  }, [activeStepId]);

  if (!showQuickStartGuide) return null;
  if (onboardingCompleteTime < 1650870000) return null;
  if (isDismissed) return null;
  if (isZoom) return null;
  if (isMobile) return null;

  return (
    <>
      {!isComplete && (
        <div className={baseClassName}>
          <Portal idPrefix="quick-start-guide">
            <CSSTransition in={isOpen} timeout={150} classNames={`${baseClassName}__animation`} unmountOnExit>
              <div className={`${baseClassName}__panel`}>
                <div className={listPanelClasses}>
                  <QsgList
                    setActiveStepId={setActiveStepId}
                    toggleIsOpen={toggleIsOpen}
                    quickStartGuideData={quickStartGuideData}
                  />
                </div>
                <div className={`${baseClassName}__panel__item`}>
                  <QsgStep setActiveStepId={setActiveStepId} activeStepId={activeStepId} />
                </div>
              </div>
            </CSSTransition>
          </Portal>
          <div className={`${baseClassName}__button`} role="button" onClick={toggleIsOpen}>
            <span className={`${baseClassName}__button__icon`}>
              <WpIcon size="lg" name="map-marker-smile" />
            </span>
            Quick Start Guide
          </div>
        </div>
      )}
      <QuickStartGuideCompletionModal isOpen={isCompleteModalOpen} toggleIsOpen={toggleIsCompleteModalOpen} />
    </>
  );
};

export default memo(QuickStartGuide);
